import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Edit, Trash2, Search, Building2, MapPin, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, X, Mail, Loader2, UserCog, Users, KeyRound, Shield } from "lucide-react";

const EMPTY = {
  client_code: "", name: "", trade_name: "", address: "", city: "", state: "", zip_code: "",
  phone: "", celular: "", email: "", website: "", contact_name: "",
  nit: "", nrc: "", fiscal_position: "", id_type: "", economic_activity: "",
  tags: [], invoice_doc_type: "", payment_terms: "", salesperson: "", password: "",
  has_branches: false,
  zona_verde: "", zona_amarilla: "", zona_roja: "", areas_actividad_plaga: "",
  vegetacion_externa: false, mascotas_ninos_adultos: false,
};

const SV_DEPARTMENTS = [
  "San Salvador", "La Libertad", "Santa Ana", "San Miguel", "Sonsonate",
  "Usulután", "Ahuachapán", "La Paz", "La Unión", "Chalatenango",
  "Cuscatlán", "Morazán", "San Vicente", "Cabañas",
];

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showTech, setShowTech] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  // Service addresses (branches)
  const [branches, setBranches] = useState([]);
  const [newBranch, setNewBranch] = useState({ name: "", odoo_contact_code: "", address: "", city: "", state: "", phone: "", contact_name: "" });
  // Import state
  const [showImport, setShowImport] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickForm, setQuickForm] = useState({ cod_facturacion: "", nombre_facturacion: "", cod_servicio: "", nombre_servicio: "" });
  const [quickSaving, setQuickSaving] = useState(false);

  const [umEnabled, setUmEnabled] = useState(new Set());
  const load = () => api.get("/clients").then((r) => setItems(r.data));
  const loadUmStatus = () => api.get("/clients/user-management/status")
    .then((r) => setUmEnabled(new Set(r.data?.enabled_client_ids || [])))
    .catch(() => {});
  useEffect(() => { load(); loadUmStatus(); }, []);

  const submitQuickAdd = async () => {
    const { cod_facturacion, nombre_facturacion, cod_servicio, nombre_servicio } = quickForm;
    if (!cod_facturacion.trim() || !nombre_facturacion.trim()) return toast.error("Código y nombre de facturación son obligatorios");
    if (!cod_servicio.trim() || !nombre_servicio.trim()) return toast.error("Código y nombre de servicio son obligatorios");
    setQuickSaving(true);
    try {
      await api.post("/clients/quick-add", quickForm);
      toast.success("Cliente y sucursal creados");
      setShowQuickAdd(false);
      setQuickForm({ cod_facturacion: "", nombre_facturacion: "", cod_servicio: "", nombre_servicio: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    } finally {
      setQuickSaving(false);
    }
  };

  const openCreate = () => {
    setEditing(null); setForm(EMPTY); setBranches([]); setShowTech(false); setShowOptional(false);
    setNewBranch({ name: "", odoo_contact_code: "", address: "", city: "", state: "", phone: "", contact_name: "" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c); setForm({ ...EMPTY, ...c, password: "", tags: c.tags || [] });
    setShowTech(false); setShowOptional(false);
    // Load branches
    api.get(`/branches?client_id=${c.id}`).then(r => setBranches(r.data || [])).catch(() => setBranches([]));
    setOpen(true);
  };

  const f = (k) => form[k] || "";
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    // Validations
    if (!form.name?.trim()) return toast.error("Nombre (razón social) es obligatorio");
    if (!form.client_code?.trim()) return toast.error("Código de contacto Odoo es obligatorio");
    if (!form.address?.trim()) return toast.error("Dirección es obligatoria");
    if (!form.city?.trim()) return toast.error("Ciudad es obligatoria");
    if (!form.state?.trim()) return toast.error("Departamento es obligatorio");
    if (!form.phone?.trim() && !form.celular?.trim()) return toast.error("Teléfono o celular es obligatorio");

    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      let clientId;
      if (editing) {
        await api.put(`/clients/${editing.id}`, payload);
        clientId = editing.id;
      } else {
        const res = await api.post("/clients", payload);
        clientId = res.data.id;
      }
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminar a ${c.name}?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const [sendingCredsId, setSendingCredsId] = useState(null);
  const sendCredentials = async (c) => {
    if (!c.email) { toast.error("El cliente no tiene correo. Agrégalo en la ficha del cliente."); return; }
    if (!window.confirm(`Enviar credenciales de acceso al portal a ${c.email}?\n\nEsto generará una nueva contraseña para el cliente.`)) return;
    setSendingCredsId(c.id);
    try {
      const { data } = await api.post(`/clients/${c.id}/send-credentials`);
      toast.success(`Credenciales enviadas a ${data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el correo");
    } finally { setSendingCredsId(null); }
  };

  const [togglingUmId, setTogglingUmId] = useState(null);
  const toggleUserManagement = async (c) => {
    setTogglingUmId(c.id);
    try {
      const { data: state } = await api.get(`/clients/${c.id}/user-management`);
      const next = !state.enabled;
      if (next && !state.has_user) {
        toast.error("Primero envíe las credenciales del portal a este cliente.");
        return;
      }
      await api.post(`/clients/${c.id}/user-management?enabled=${next}`);
      setUmEnabled((prev) => {
        const s = new Set(prev);
        if (next) s.add(c.id); else s.delete(c.id);
        return s;
      });
      toast.success(next
        ? "Gestión de usuarios habilitada. El cliente ya puede crear usuarios en su portal."
        : "Gestión de usuarios deshabilitada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally { setTogglingUmId(null); }
  };

  const [usersClient, setUsersClient] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [clientBranches, setClientBranches] = useState([]);
  const [userForm, setUserForm] = useState(null); // null=closed; {} = create/edit form state
  const [savingUser, setSavingUser] = useState(false);
  const [uBranchQuery, setUBranchQuery] = useState("");
  const [branchesLoading, setBranchesLoading] = useState(false);
  const genPw = () => { const c = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"; return Array.from({ length: 10 }, () => c[Math.floor(Math.random() * c.length)]).join(""); };
  const reloadUsers = async (cid) => {
    const { data } = await api.get(`/clients/${cid}/portal-users`);
    setUsersData(data);
  };
  const openUsers = async (c) => {
    setUsersClient(c);
    setUsersData(null);
    setUserForm(null);
    setClientBranches([]);
    setUsersLoading(true);
    try {
      const { data } = await api.get(`/clients/${c.id}/portal-users`);
      setUsersData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los usuarios");
    } finally { setUsersLoading(false); }
  };
  const ensureBranches = async (cid) => {
    if (clientBranches.length > 0) return;
    setBranchesLoading(true);
    try {
      const { data } = await api.get(`/branches?client_id=${cid}`);
      setClientBranches(data || []);
    } catch { /* ignore */ }
    finally { setBranchesLoading(false); }
  };
  const openUserCreate = () => { setUBranchQuery(""); ensureBranches(usersClient.id); setUserForm({ mode: "create", full_name: "", email: "", password: genPw(), branch_scope: "all", branch_ids: [] }); };
  const openPrimaryCreate = () => { setUserForm({ mode: "primary", full_name: usersClient?.name || "", email: "", password: genPw(), send_email: true }); };
  const openUserEdit = (u) => { setUBranchQuery(""); ensureBranches(usersClient.id); setUserForm({ mode: "edit", id: u.id, full_name: u.full_name, branch_scope: u.branch_scope, branch_ids: (u.branch_ids || []) }); };
  const toggleUFormBranch = (id) => setUserForm((f) => ({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id] }));
  const submitUserForm = async () => {
    if (!userForm.full_name.trim()) return toast.error("Ingrese el nombre");
    if (userForm.mode !== "primary" && userForm.branch_scope === "specific" && userForm.branch_ids.length === 0) return toast.error("Seleccione al menos una sucursal");
    setSavingUser(true);
    try {
      if (userForm.mode === "primary") {
        if (!userForm.email.trim()) return toast.error("Ingrese un correo");
        const res = await api.post(`/clients/${usersClient.id}/primary-user`, {
          full_name: userForm.full_name, email: userForm.email.trim().toLowerCase(),
          password: userForm.password, send_email: userForm.send_email,
        });
        toast.success(res.data?.email_sent ? "Usuario principal creado. Credenciales enviadas por correo." : "Usuario principal creado.");
      } else if (userForm.mode === "create") {
        if (!userForm.email.trim()) return toast.error("Ingrese un correo");
        const res = await api.post(`/clients/${usersClient.id}/portal-users`, {
          full_name: userForm.full_name, email: userForm.email.trim().toLowerCase(),
          password: userForm.password, branch_scope: userForm.branch_scope, branch_ids: userForm.branch_ids,
        });
        toast.success(res.data?.email_sent ? "Usuario creado. Credenciales enviadas por correo." : "Usuario creado.");
      } else {
        await api.patch(`/clients/${usersClient.id}/portal-users/${userForm.id}`, {
          full_name: userForm.full_name, branch_scope: userForm.branch_scope, branch_ids: userForm.branch_ids,
        });
        toast.success("Usuario actualizado");
      }
      setUserForm(null);
      reloadUsers(usersClient.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSavingUser(false); }
  };
  const toggleUserActive = async (u) => {
    try {
      await api.patch(`/clients/${usersClient.id}/portal-users/${u.id}`, { is_active: !u.is_active });
      reloadUsers(usersClient.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
  };
  const resetUserPassword = async (u) => {
    if (!window.confirm(`¿Generar una nueva contraseña para ${u.full_name}?`)) return;
    try {
      const { data } = await api.post(`/clients/${usersClient.id}/portal-users/${u.id}/reset-password`);
      window.prompt("Nueva contraseña (cópiela y compártala):", data.password);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo resetear");
    }
  };

  const addBranch = async () => {
    if (!newBranch.name?.trim()) return toast.error("Nombre de sucursal es obligatorio");
    if (!editing) return toast.error("Guarde el cliente primero antes de agregar sucursales");
    try {
      const payload = { ...newBranch, client_id: editing.id };
      await api.post("/branches", payload);
      // Also set has_branches on client
      if (!form.has_branches) {
        await api.put(`/clients/${editing.id}`, { ...form, has_branches: true });
        set("has_branches", true);
      }
      const r = await api.get(`/branches?client_id=${editing.id}`);
      setBranches(r.data || []);
      setNewBranch({ name: "", odoo_contact_code: "", address: "", city: "", state: "", phone: "", contact_name: "" });
      toast.success("Sucursal agregada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const removeBranch = async (bId) => {
    try {
      await api.delete(`/branches/${bId}`);
      setBranches(prev => prev.filter(b => b.id !== bId));
      toast.success("Sucursal eliminada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/clients/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_clientes_truly360.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error al descargar plantilla");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportLoading(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/clients/import/preview", fd);
      setImportPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al procesar archivo");
    } finally { setImportLoading(false); }
    e.target.value = "";
  };

  const executeImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await api.post("/clients/import/execute", fd);
      const d = res.data;
      let msg = `Creados: ${d.created_clients} clientes, ${d.created_branches} sucursales`;
      if (d.skipped_branches > 0) msg += ` · ${d.skipped_branches} sucursales ya existían`;
      if (d.skipped_rows > 0) msg += ` · ${d.skipped_rows} filas omitidas`;
      toast.success(msg, { duration: 6000 });
      setShowImport(false);
      setImportPreview(null);
      setImportFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally { setImporting(false); }
  };

  const filtered = items.filter((c) =>
    !q || c.name?.toLowerCase().includes(q.toLowerCase()) || c.client_code?.includes(q) || c.trade_name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-clients">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Clientes</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowImport(true); setImportPreview(null); setImportFile(null); }} data-testid="admin-clients-import">
            <Upload className="w-4 h-4 mr-1" /> Importar Excel
          </Button>
          <Button variant="outline" onClick={() => setShowQuickAdd(true)} data-testid="admin-clients-quick-add">
            <Plus className="w-4 h-4 mr-1" /> Agregar rápido
          </Button>
          <Button onClick={openCreate} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="admin-clients-new">
            <Plus className="w-4 h-4 mr-1" /> Nuevo cliente
          </Button>
        </div>
      </div>
      <div className="relative max-w-sm mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, código o nombre comercial" className="pl-9" data-testid="admin-clients-search" />
      </div>
      <Card data-testid="admin-clients-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead><TableHead>Razón Social</TableHead><TableHead>Nombre Comercial</TableHead><TableHead>Ciudad</TableHead><TableHead>Tel.</TableHead><TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Sin clientes</TableCell></TableRow>}
            {filtered.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono text-xs">{c.client_code}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.trade_name || "—"}</TableCell>
                <TableCell className="text-sm">{c.city || "—"}{c.has_branches && <Badge variant="outline" className="ml-1.5 text-[9px]">Sucursales</Badge>}</TableCell>
                <TableCell className="font-mono text-sm">{c.phone || c.celular || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="icon" onClick={() => sendCredentials(c)} disabled={sendingCredsId === c.id} title="Enviar credenciales al portal" data-testid={`admin-clients-send-creds-${c.id}`}>
                    {sendingCredsId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 text-blue-600" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => toggleUserManagement(c)} disabled={togglingUmId === c.id} title={umEnabled.has(c.id) ? "Gestión de usuarios habilitada (clic para deshabilitar)" : "Gestión de usuarios deshabilitada (clic para habilitar)"} data-testid={`admin-clients-user-mgmt-${c.id}`}>
                    {togglingUmId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCog className={`w-4 h-4 ${umEnabled.has(c.id) ? "text-emerald-600" : "text-red-500"}`} />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openUsers(c)} title="Ver usuarios del cliente" data-testid={`admin-clients-view-users-${c.id}`}>
                    <Users className="w-4 h-4 text-indigo-600" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`admin-clients-edit-${c.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`admin-clients-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* ═══ CREATE/EDIT DIALOG ═══ */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* REQUIRED FIELDS */}
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Datos principales *</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nombre (razón social) *</Label>
                  <Input value={f("name")} onChange={e => set("name", e.target.value)} data-testid="client-form-name" />
                </div>
                <div>
                  <Label className="text-xs">Nombre comercial</Label>
                  <Input value={f("trade_name")} onChange={e => set("trade_name", e.target.value)} data-testid="client-form-trade_name" />
                </div>
                <div>
                  <Label className="text-xs">Código de contacto Odoo *</Label>
                  <Input value={f("client_code")} onChange={e => set("client_code", e.target.value)} placeholder="ej: 21586" data-testid="client-form-client_code" />
                </div>
                <div>
                  <Label className="text-xs">Dirección (calle) *</Label>
                  <Input value={f("address")} onChange={e => set("address", e.target.value)} data-testid="client-form-address" />
                </div>
                <div>
                  <Label className="text-xs">Ciudad *</Label>
                  <Input value={f("city")} onChange={e => set("city", e.target.value)} data-testid="client-form-city" />
                </div>
                <div>
                  <Label className="text-xs">Departamento *</Label>
                  <Select value={f("state")} onValueChange={v => set("state", v)}>
                    <SelectTrigger data-testid="client-form-state"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      {SV_DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Teléfono *</Label>
                  <Input value={f("phone")} onChange={e => set("phone", e.target.value)} data-testid="client-form-phone" />
                </div>
                <div>
                  <Label className="text-xs">Celular</Label>
                  <Input value={f("celular")} onChange={e => set("celular", e.target.value)} data-testid="client-form-celular" />
                </div>
              </div>
            </div>

            {/* OPTIONAL FIELDS (collapsible) */}
            <div className="border-t pt-2">
              <button onClick={() => setShowOptional(!showOptional)} className="flex items-center gap-2 w-full text-left py-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Datos adicionales (opcional)</p>
                {showOptional ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              {showOptional && (
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs">Código postal</Label>
                    <Input value={f("zip_code")} onChange={e => set("zip_code", e.target.value)} data-testid="client-form-zip_code" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={f("email")} onChange={e => set("email", e.target.value)} data-testid="client-form-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Sitio web</Label>
                    <Input value={f("website")} onChange={e => set("website", e.target.value)} data-testid="client-form-website" />
                  </div>
                  <div>
                    <Label className="text-xs">Contacto principal</Label>
                    <Input value={f("contact_name")} onChange={e => set("contact_name", e.target.value)} data-testid="client-form-contact_name" />
                  </div>
                  <div>
                    <Label className="text-xs">NIT</Label>
                    <Input value={f("nit")} onChange={e => set("nit", e.target.value)} data-testid="client-form-nit" />
                  </div>
                  <div>
                    <Label className="text-xs">NRC</Label>
                    <Input value={f("nrc")} onChange={e => set("nrc", e.target.value)} data-testid="client-form-nrc" />
                  </div>
                  <div>
                    <Label className="text-xs">Posición fiscal</Label>
                    <Input value={f("fiscal_position")} onChange={e => set("fiscal_position", e.target.value)} data-testid="client-form-fiscal_position" />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo de identificación</Label>
                    <Input value={f("id_type")} onChange={e => set("id_type", e.target.value)} data-testid="client-form-id_type" />
                  </div>
                  <div>
                    <Label className="text-xs">Actividad económica</Label>
                    <Input value={f("economic_activity")} onChange={e => set("economic_activity", e.target.value)} data-testid="client-form-economic_activity" />
                  </div>
                  <div>
                    <Label className="text-xs">Doc. ventas</Label>
                    <Select value={f("invoice_doc_type")} onValueChange={v => set("invoice_doc_type", v)}>
                      <SelectTrigger data-testid="client-form-invoice_doc_type"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CCF">CCF</SelectItem>
                        <SelectItem value="Factura">Factura</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Condición de pago</Label>
                    <Input value={f("payment_terms")} onChange={e => set("payment_terms", e.target.value)} data-testid="client-form-payment_terms" />
                  </div>
                  <div>
                    <Label className="text-xs">Vendedor</Label>
                    <Input value={f("salesperson")} onChange={e => set("salesperson", e.target.value)} data-testid="client-form-salesperson" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Contraseña portal (opcional)</Label>
                    <Input type="password" value={form.password || ""} onChange={e => set("password", e.target.value)} placeholder="Deje en blanco para no cambiar" data-testid="client-form-password" />
                  </div>
                </div>
              )}
            </div>

            {/* SERVICE ADDRESSES / BRANCHES (only in edit mode) */}
            {editing && (
              <div className="border-t pt-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Direcciones de servicio (sucursales)
                </p>
                {branches.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {branches.map(b => (
                      <div key={b.id} className="flex items-center gap-2 rounded-lg border p-2.5 bg-secondary/30">
                        <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{b.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {b.odoo_contact_code && <span className="font-mono mr-2">{b.odoo_contact_code}</span>}
                            {b.address && <span>{b.address}</span>}
                            {b.city && <span>, {b.city}</span>}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeBranch(b.id)} className="shrink-0">
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="rounded-lg border p-3 bg-secondary/10 space-y-2">
                  <p className="text-xs text-muted-foreground">Agregar nueva sucursal:</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input placeholder="Nombre sucursal *" value={newBranch.name} onChange={e => setNewBranch(b => ({...b, name: e.target.value}))} className="text-sm" />
                    <Input placeholder="Código contacto Odoo" value={newBranch.odoo_contact_code} onChange={e => setNewBranch(b => ({...b, odoo_contact_code: e.target.value}))} className="text-sm" />
                    <Input placeholder="Dirección" value={newBranch.address} onChange={e => setNewBranch(b => ({...b, address: e.target.value}))} className="text-sm" />
                    <Input placeholder="Ciudad" value={newBranch.city} onChange={e => setNewBranch(b => ({...b, city: e.target.value}))} className="text-sm" />
                    <Select value={newBranch.state} onValueChange={v => setNewBranch(b => ({...b, state: v}))}>
                      <SelectTrigger className="text-sm"><SelectValue placeholder="Departamento" /></SelectTrigger>
                      <SelectContent>{SV_DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Teléfono" value={newBranch.phone} onChange={e => setNewBranch(b => ({...b, phone: e.target.value}))} className="text-sm" />
                  </div>
                  <Button size="sm" onClick={addBranch} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" data-testid="client-add-branch">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Agregar sucursal
                  </Button>
                </div>
              </div>
            )}

            {/* TECHNICAL INFO (collapsible) */}
            <div className="border-t pt-2">
              <button onClick={() => setShowTech(!showTech)} className="flex items-center gap-2 w-full text-left py-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Información técnica del sitio</p>
                {showTech ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              {showTech && (
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  {[
                    ["zona_verde", "Zona Verde (bajo riesgo)"],
                    ["zona_amarilla", "Zona Amarilla (riesgo medio)"],
                    ["zona_roja", "Zona Roja (alto riesgo)"],
                    ["areas_actividad_plaga", "Áreas con actividad de plaga"],
                  ].map(([k, lbl]) => (
                    <div key={k}>
                      <Label className="text-xs">{lbl}</Label>
                      <Textarea rows={2} value={f(k)} onChange={e => set(k, e.target.value)} className="text-sm resize-none" data-testid={`client-form-${k}`} />
                    </div>
                  ))}
                  <div className="sm:col-span-2 flex flex-col gap-3">
                    <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                      <Label htmlFor="sw-veg" className="text-sm cursor-pointer">Vegetación externa presente</Label>
                      <Switch id="sw-veg" checked={!!form.vegetacion_externa} onCheckedChange={v => set("vegetacion_externa", v)} />
                    </div>
                    <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                      <Label htmlFor="sw-masc" className="text-sm cursor-pointer">Mascotas / niños / adultos mayores</Label>
                      <Switch id="sw-masc" checked={!!form.mascotas_ninos_adultos} onCheckedChange={v => set("mascotas_ninos_adultos", v)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="client-form-submit">
              {editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ IMPORT DIALOG ═══ */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" /> Importar clientes desde Excel
            </DialogTitle>
            <DialogDescription>
              Suba un archivo Excel con 4 columnas: Cod. y Nombre de Facturación + Cod. y Nombre de Servicio (sucursal).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={downloadTemplate} data-testid="import-download-template">
                <Download className="w-4 h-4 mr-1.5" /> Descargar plantilla
              </Button>
              <span className="text-xs text-muted-foreground">o</span>
              <Button variant="outline" onClick={() => importRef.current?.click()} disabled={importLoading} data-testid="import-upload-btn">
                <Upload className="w-4 h-4 mr-1.5" /> {importLoading ? "Procesando..." : "Subir archivo"}
              </Button>
              <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} />
            </div>

            {importFile && !importLoading && (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                {importFile.name}
              </div>
            )}

            {importPreview && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1 rounded-lg border p-3 text-center">
                    <div className="text-2xl font-bold">{importPreview.total}</div>
                    <div className="text-xs text-muted-foreground">Filas (sucursales)</div>
                  </div>
                  <div className="flex-1 rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{importPreview.new_clients || 0}</div>
                    <div className="text-xs text-blue-700">Clientes nuevos</div>
                  </div>
                  <div className="flex-1 rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{importPreview.valid}</div>
                    <div className="text-xs text-green-700">Válidas</div>
                  </div>
                  <div className={`flex-1 rounded-lg border p-3 text-center ${importPreview.invalid > 0 ? "border-red-200 bg-red-50" : "border-border"}`}>
                    <div className={`text-2xl font-bold ${importPreview.invalid > 0 ? "text-red-700" : ""}`}>{importPreview.invalid}</div>
                    <div className={`text-xs ${importPreview.invalid > 0 ? "text-red-700" : "text-muted-foreground"}`}>Con errores</div>
                  </div>
                </div>

                <div className="max-h-[300px] overflow-y-auto rounded-lg border">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Fila</th>
                        <th className="px-2 py-1.5 text-left">Cod. Fact.</th>
                        <th className="px-2 py-1.5 text-left">Cliente Fact.</th>
                        <th className="px-2 py-1.5 text-left">Cod. Serv.</th>
                        <th className="px-2 py-1.5 text-left">Sucursal</th>
                        <th className="px-2 py-1.5 text-left">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r, i) => (
                        <tr key={i} className={r.errors.length > 0 ? "bg-red-50" : ""}>
                          <td className="px-2 py-1.5 font-mono">{r.row}</td>
                          <td className="px-2 py-1.5 font-mono">{r.data.cod_facturacion}</td>
                          <td className="px-2 py-1.5 text-muted-foreground">{r.data.nombre_facturacion}</td>
                          <td className="px-2 py-1.5 font-mono font-medium">{r.data.cod_servicio}</td>
                          <td className="px-2 py-1.5">{r.data.nombre_servicio}</td>
                          <td className="px-2 py-1.5">
                            {r.errors.length === 0 ? (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            ) : (
                              <div className="flex items-start gap-1">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
                                <div className="space-y-0.5">
                                  {r.errors.map((e, j) => <div key={j} className="text-red-600">{e}</div>)}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Cancelar</Button>
            {importPreview && importPreview.valid > 0 && (
              <Button
                onClick={executeImport}
                disabled={importing}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                data-testid="import-execute-btn"
              >
                {importing ? "Importando..." : `Importar ${importPreview.new_clients || 0} clientes + ${importPreview.valid - (importPreview.rows?.filter(r => r.errors?.length > 0).length || 0)} sucursales nuevas`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ QUICK ADD DIALOG ═══ */}
      <Dialog open={showQuickAdd} onOpenChange={setShowQuickAdd}>
        <DialogContent className="max-w-md" data-testid="quick-add-dialog">
          <DialogHeader>
            <DialogTitle>Agregar cliente rápido</DialogTitle>
            <DialogDescription>Crea un cliente de facturación con su sucursal de servicio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cliente de facturación</p>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <div>
                  <Label className="text-xs">Código *</Label>
                  <Input value={quickForm.cod_facturacion} onChange={e => setQuickForm(p => ({ ...p, cod_facturacion: e.target.value }))} placeholder="38825" data-testid="quick-cod-fact" />
                </div>
                <div>
                  <Label className="text-xs">Nombre *</Label>
                  <Input value={quickForm.nombre_facturacion} onChange={e => setQuickForm(p => ({ ...p, nombre_facturacion: e.target.value }))} placeholder="SPECIALTY FOODS, S.A. DE C.V." data-testid="quick-nom-fact" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cliente de servicio (sucursal)</p>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <div>
                  <Label className="text-xs">Código *</Label>
                  <Input value={quickForm.cod_servicio} onChange={e => setQuickForm(p => ({ ...p, cod_servicio: e.target.value }))} placeholder="42597" data-testid="quick-cod-serv" />
                </div>
                <div>
                  <Label className="text-xs">Nombre *</Label>
                  <Input value={quickForm.nombre_servicio} onChange={e => setQuickForm(p => ({ ...p, nombre_servicio: e.target.value }))} placeholder="WENDY'S CENTRO HISTORICO" data-testid="quick-nom-serv" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickAdd(false)}>Cancelar</Button>
            <Button onClick={submitQuickAdd} disabled={quickSaving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="quick-add-submit">
              {quickSaving ? "Creando..." : "Crear cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal users of a client (admin view) */}
      <Dialog open={!!usersClient} onOpenChange={(v) => { if (!v) { setUsersClient(null); setUsersData(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="admin-client-users-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Usuarios de {usersClient?.name}</DialogTitle>
            <DialogDescription>
              {usersData?.user_management_enabled
                ? "Este cliente tiene habilitada la gestión de usuarios."
                : "Aún puede administrar los usuarios de este cliente desde aquí."}
            </DialogDescription>
          </DialogHeader>
          {!userForm && !usersLoading && (
            <div className="flex justify-end gap-2">
              {!(usersData?.users || []).some((x) => x.is_primary) && (
                <Button size="sm" variant="secondary" onClick={openPrimaryCreate} data-testid="admin-client-primary-new"><Shield className="w-4 h-4 mr-1" /> Crear usuario principal</Button>
              )}
              <Button size="sm" onClick={openUserCreate} data-testid="admin-client-user-new"><Plus className="w-4 h-4 mr-1" /> Nuevo usuario</Button>
            </div>
          )}
          {userForm ? (
            <div className="space-y-3 border rounded-lg p-3" data-testid="admin-client-user-form">
              <div className="font-medium text-sm">{userForm.mode === "primary" ? "Crear usuario principal" : userForm.mode === "create" ? "Nuevo usuario" : "Editar usuario"}</div>
              <div>
                <Label className="text-xs">Nombre completo</Label>
                <Input value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} data-testid="admin-user-name" />
              </div>
              {(userForm.mode === "create" || userForm.mode === "primary") && (
                <>
                  <div>
                    <Label className="text-xs">Correo</Label>
                    <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="admin-user-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Contraseña</Label>
                    <div className="flex gap-2">
                      <Input value={userForm.password} readOnly className="bg-muted/50 font-mono" data-testid="admin-user-pw" />
                      <Button type="button" variant="outline" size="icon" title="Generar" onClick={() => setUserForm({ ...userForm, password: genPw() })}><KeyRound className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {userForm.mode === "primary" && (
                    <div className="flex items-center gap-2">
                      <Switch checked={userForm.send_email} onCheckedChange={(v) => setUserForm({ ...userForm, send_email: v })} data-testid="admin-user-send-email" />
                      <Label className="text-xs font-normal">Enviar credenciales por correo</Label>
                    </div>
                  )}
                </>
              )}
              {userForm.mode !== "primary" && (
              <div>
                <Label className="text-xs mb-1 block">Acceso a sucursales</Label>
                <RadioGroup value={userForm.branch_scope} onValueChange={(v) => setUserForm({ ...userForm, branch_scope: v })} className="space-y-1">
                  <div className="flex items-center gap-2"><RadioGroupItem value="all" id="au-all" data-testid="admin-user-scope-all" /><Label htmlFor="au-all" className="font-normal cursor-pointer">Todas las sucursales</Label></div>
                  <div className="flex items-center gap-2"><RadioGroupItem value="specific" id="au-spec" data-testid="admin-user-scope-specific" /><Label htmlFor="au-spec" className="font-normal cursor-pointer">Sucursales específicas</Label></div>
                </RadioGroup>
                {userForm.branch_scope === "specific" && (
                  <div className="mt-2 space-y-2">
                    <Input value={uBranchQuery} onChange={(e) => setUBranchQuery(e.target.value)} placeholder="Buscar sucursal…" className="h-9" data-testid="admin-user-branch-search" />
                    <div className="max-h-40 overflow-auto border rounded-lg p-2 space-y-1">
                      {clientBranches.filter((b) => (b.name || "").toLowerCase().includes(uBranchQuery.trim().toLowerCase())).map((b) => (
                        <label key={b.id} className="flex items-center gap-2 py-1 cursor-pointer">
                          <Checkbox checked={userForm.branch_ids.includes(b.id)} onCheckedChange={() => toggleUFormBranch(b.id)} data-testid={`admin-user-branch-${b.id}`} />
                          <span className="text-sm">{b.name}</span>
                        </label>
                      ))}
                      {clientBranches.length === 0 && <p className="text-xs text-muted-foreground p-1">{branchesLoading ? "Cargando sucursales…" : "Este cliente no tiene sucursales."}</p>}
                    </div>
                  </div>
                )}
              </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setUserForm(null)}>Cancelar</Button>
                <Button size="sm" onClick={submitUserForm} disabled={savingUser} data-testid="admin-user-save">{savingUser ? "Guardando…" : "Guardar"}</Button>
              </div>
            </div>
          ) : usersLoading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Cargando…</p>
          ) : (usersData?.users?.length ? (
            <div className="space-y-2 max-h-[55vh] overflow-auto">
              {usersData.users.map((u) => (
                <div key={u.id} className="border rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`admin-client-user-${u.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium flex items-center gap-2">
                      {u.full_name}
                      {u.is_primary && <Badge variant="outline" className="text-[10px]">Principal</Badge>}
                      {u.is_client_admin && <Badge variant="secondary" className="gap-1 text-[10px]"><Shield className="w-3 h-3" /> Administrador</Badge>}
                      {!u.is_active && <Badge variant="destructive" className="text-[10px]">Inactivo</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{u.email || u.username || "—"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {u.branch_scope === "all" ? "Todas las sucursales" : (u.branch_names.join(", ") || "Sin sucursales")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!u.is_client_admin && (
                      <Button variant="outline" size="sm" onClick={() => openUserEdit(u)} data-testid={`admin-client-user-edit-${u.id}`}><Edit className="w-4 h-4 mr-1" /> Editar</Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => resetUserPassword(u)} data-testid={`admin-client-user-reset-${u.id}`}><KeyRound className="w-4 h-4 mr-1" /> Contraseña</Button>
                    <div className="flex items-center gap-1.5"><Switch checked={u.is_active} onCheckedChange={() => toggleUserActive(u)} data-testid={`admin-client-user-active-${u.id}`} /><span className="text-xs text-muted-foreground">Activo</span></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-6 text-center">Este cliente aún no tiene usuarios de portal. Cree el primero con "Nuevo usuario" o envíe credenciales.</p>
          ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUsersClient(null); setUsersData(null); }}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
